import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["label", "children", "size", "placement", "open", "defaultFocusElement", "contentRef", "shouldContainFocus", "shouldReturnFocus", "shouldCloseOnDocumentClick", "onOpen", "onClose", "onDismiss", "mountNode", "insertAt", "liveRegion", "onEnter", "onEntering", "onEntered", "onExit", "onExiting", "onExited", "onTransition", "transitionOnMount", "transitionEnter", "transitionExit", "border", "shadow", "role", "enableMask"];
var _dec, _dec2, _dec3, _class, _Tray;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { Dialog } from '@instructure/ui-dialog';
import { omitProps } from '@instructure/ui-react-utils';
import { createChainedFunction } from '@instructure/ui-utils';
import { textDirectionContextConsumer } from '@instructure/ui-i18n';
import { testable } from '@instructure/ui-testable';
import { Portal } from '@instructure/ui-portal';
import { mirrorHorizontalPlacement } from '@instructure/ui-position';
import { Transition } from '@instructure/ui-motion';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { propTypes, allowedProps } from './props';
import { Mask } from '@instructure/ui-overlays';

/**
---
category: components
---
**/
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
let Tray = (_dec = withStyle(generateStyle, generateComponentTheme), _dec2 = textDirectionContextConsumer(), _dec3 = testable(), _dec(_class = _dec2(_class = _dec3(_class = (_Tray = class Tray extends Component {
  constructor(props) {
    var _props$open;
    super(props);
    this.ref = null;
    this.dialogRef = null;
    this.state = void 0;
    this.handleTransitionComplete = _type => {
      this.setState({
        transitioning: false
      });
    };
    this.handlePortalOpen = DOMNode => {
      if (DOMNode) {
        this.DOMNode = DOMNode;
      }
    };
    this.state = {
      transitioning: false,
      open: (_props$open = props.open) !== null && _props$open !== void 0 ? _props$open : false
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
  }
  componentDidUpdate(prevProps, _prevState) {
    var _this$props$makeStyle2, _this$props2;
    if (this.props.open !== prevProps.open) {
      if (!this.props.open) {
        var _this$dialogRef;
        // calling Dialog.close() to remove focusregion immediately when transition starts
        // so if a new Tray is opened (during transition) the new focusregion won't get treated as a child of this one
        (_this$dialogRef = this.dialogRef) === null || _this$dialogRef === void 0 ? void 0 : _this$dialogRef.close();
      }
      this.setState({
        transitioning: true,
        open: this.props.open
      });
    }
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
  }
  get placement() {
    const _this$props3 = this.props,
      placement = _this$props3.placement,
      dir = _this$props3.dir;
    const isRtl = dir === textDirectionContextConsumer.DIRECTION.rtl;
    return isRtl ? mirrorHorizontalPlacement(placement, ' ') : placement;
  }
  get direction() {
    switch (this.placement) {
      case 'top':
        return 'up';
      case 'bottom':
        return 'down';
      case 'end':
        return 'right';
      default:
        // start
        return 'left';
    }
  }
  get transition() {
    return `slide-${this.direction}`;
  }
  get DOMNode() {
    // The DOMNode property is needed for Portal type components
    return this.ref;
  }
  set DOMNode(el) {
    this.ref = el ? el : null;
  }
  render() {
    var _this$props$styles, _this$props$styles2;
    const _this$props4 = this.props,
      label = _this$props4.label,
      children = _this$props4.children,
      size = _this$props4.size,
      placement = _this$props4.placement,
      open = _this$props4.open,
      defaultFocusElement = _this$props4.defaultFocusElement,
      contentRef = _this$props4.contentRef,
      shouldContainFocus = _this$props4.shouldContainFocus,
      shouldReturnFocus = _this$props4.shouldReturnFocus,
      shouldCloseOnDocumentClick = _this$props4.shouldCloseOnDocumentClick,
      onOpen = _this$props4.onOpen,
      onClose = _this$props4.onClose,
      onDismiss = _this$props4.onDismiss,
      mountNode = _this$props4.mountNode,
      insertAt = _this$props4.insertAt,
      liveRegion = _this$props4.liveRegion,
      onEnter = _this$props4.onEnter,
      onEntering = _this$props4.onEntering,
      onEntered = _this$props4.onEntered,
      onExit = _this$props4.onExit,
      onExiting = _this$props4.onExiting,
      onExited = _this$props4.onExited,
      onTransition = _this$props4.onTransition,
      transitionOnMount = _this$props4.transitionOnMount,
      transitionEnter = _this$props4.transitionEnter,
      transitionExit = _this$props4.transitionExit,
      border = _this$props4.border,
      shadow = _this$props4.shadow,
      role = _this$props4.role,
      enableMask = _this$props4.enableMask,
      props = _objectWithoutProperties(_this$props4, _excluded);
    const portalIsOpen = this.state.open || this.state.transitioning;
    const content = _jsx(Transition, {
      in: open,
      type: this.transition,
      onTransition: onTransition,
      onEnter: onEnter,
      onEntering: onEntering,
      onEntered: createChainedFunction(this.handleTransitionComplete, onEntered, onOpen),
      onExit: onExit,
      onExiting: onExiting,
      onExited: createChainedFunction(this.handleTransitionComplete, onExited, onClose),
      transitionOnMount: transitionOnMount,
      transitionEnter: transitionEnter,
      transitionExit: transitionExit,
      children: _jsx("span", {
        ...omitProps(props, Tray.allowedProps),
        css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.tray,
        ref: contentRef,
        children: _jsx(Dialog, {
          ref: element => this.dialogRef = element,
          as: "div",
          label: label,
          defaultFocusElement: defaultFocusElement,
          open: true,
          shouldContainFocus: shouldContainFocus,
          shouldReturnFocus: shouldReturnFocus,
          shouldCloseOnDocumentClick: shouldCloseOnDocumentClick,
          shouldCloseOnEscape: true,
          liveRegion: liveRegion,
          onDismiss: onDismiss,
          role: role,
          children: _jsx("div", {
            css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.content,
            children: children
          })
        })
      })
    });
    return _jsx(Portal, {
      open: portalIsOpen,
      onOpen: this.handlePortalOpen,
      insertAt: insertAt,
      mountNode: mountNode,
      children: enableMask ? _jsx(Mask, {
        placement: 'center',
        fullscreen: true,
        children: content
      }) : content
    });
  }
}, _Tray.displayName = "Tray", _Tray.componentId = 'Tray', _Tray.allowedProps = allowedProps, _Tray.propTypes = propTypes, _Tray.defaultProps = {
  defaultFocusElement: null,
  open: false,
  insertAt: 'bottom',
  shouldCloseOnDocumentClick: false,
  shouldContainFocus: true,
  shouldReturnFocus: true,
  size: 'small',
  placement: 'start',
  transitionOnMount: true,
  transitionEnter: true,
  transitionExit: true,
  shadow: true,
  border: false,
  enableMask: false
}, _Tray)) || _class) || _class) || _class);
export default Tray;
export { Tray };